import { h, Component, VNode, cloneElement } from 'preact';

/**
 * Simple hash-based router for Chrome extensions
 * Does not rely on preact-router's pathname-based internals
 */

interface RouteProps {
  path: string;
  default?: boolean;
  children?: VNode | ((params: Record<string, string>) => VNode);
  [key: string]: any;
}

interface HashRouterProps {
  children: VNode<RouteProps>[];
  onChange?: (url: string) => void;
}

interface HashRouterState {
  currentPath: string;
}

// Match a route pattern against a path
function matchRoute(pattern: string, path: string): { match: boolean; params: Record<string, string> } {
  const params: Record<string, string> = {};

  // Handle query string
  const [pathOnly, queryString] = path.split('?');
  if (queryString) {
    const searchParams = new URLSearchParams(queryString);
    searchParams.forEach((value, key) => {
      params[key] = value;
    });
  }

  const patternParts = pattern.split('/').filter(Boolean);
  const pathParts = pathOnly.split('/').filter(Boolean);

  // Check if lengths match (unless pattern is shorter and path has extra segments)
  if (patternParts.length !== pathParts.length) {
    return { match: false, params: {} };
  }

  for (let i = 0; i < patternParts.length; i++) {
    const patternPart = patternParts[i];
    const pathPart = pathParts[i];

    if (patternPart.startsWith(':')) {
      // Parameter segment
      const paramName = patternPart.slice(1);
      params[paramName] = pathPart;
    } else if (patternPart !== pathPart) {
      // Static segment doesn't match
      return { match: false, params: {} };
    }
  }

  return { match: true, params };
}

export class HashRouter extends Component<HashRouterProps, HashRouterState> {
  constructor(props: HashRouterProps) {
    super(props);
    this.state = {
      currentPath: this.getHashPath()
    };
  }

  componentDidMount() {
    window.addEventListener('hashchange', this.handleHashChange);
  }

  componentWillUnmount() {
    window.removeEventListener('hashchange', this.handleHashChange);
  }

  getHashPath = (): string => {
    const hash = window.location.hash;
    return hash.slice(1) || '/'; // Remove leading # and default to /
  };

  handleHashChange = () => {
    const newPath = this.getHashPath();
    this.setState({ currentPath: newPath });

    if (this.props.onChange) {
      this.props.onChange(newPath);
    }
  };

  render() {
    const { children } = this.props;
    const { currentPath } = this.state;

    let matchedChild: VNode | null = null;
    let defaultChild: VNode | null = null;
    let matchedParams: Record<string, string> = {};

    // Find matching route
    const childArray = Array.isArray(children) ? children : [children];

    for (const child of childArray) {
      if (!child || !child.props) continue;

      const { path, default: isDefault } = child.props as RouteProps;

      if (isDefault) {
        defaultChild = child;
      }

      if (path) {
        const { match, params } = matchRoute(path, currentPath);
        if (match) {
          matchedChild = child;
          matchedParams = params;
          break;
        }
      }
    }

    // Use matched route or default
    const routeToRender = matchedChild || defaultChild;

    if (!routeToRender) {
      return null;
    }

    // Clone the element and pass params as props
    return cloneElement(routeToRender, { ...matchedParams });
  }
}

/**
 * Hash-aware route function
 */
export function route(url: string, replace = false) {
  if (replace) {
    const newUrl = window.location.pathname + window.location.search + '#' + url;
    window.location.replace(newUrl);
  } else {
    window.location.hash = url;
  }
}

// Re-export for compatibility
export type { RouterOnChangeArgs } from 'preact-router';
